/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Filter, X } from 'lucide-react'
import type { UserFilters as UserFiltersType } from '@/api/users/users.api'

interface UserFiltersProps {
  filters: UserFiltersType
  onFiltersChange: (filters: UserFiltersType) => void
}

export function UserFilters({ filters, onFiltersChange }: UserFiltersProps) {
  const [open, setOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState<UserFiltersType>(filters)

  // Synchronizace s props
  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const handleFilterChange = (key: keyof UserFiltersType, value: string | boolean | undefined) => {
    // Pokud je hodnota 'all', nastavíme undefined (= žádný filtr)
    const filterValue = value === 'all' ? undefined : value
    setLocalFilters((prev) => ({ ...prev, [key]: filterValue }))
  }

  const handleApplyFilters = () => {
    onFiltersChange(localFilters)
    setOpen(false)
  }

  const handleClearFilters = () => {
    const clearedFilters: UserFiltersType = {
      page: 1,
      perPage: 20,
      sortBy: 'email',
      sortOrder: 'ASC',
    }
    setLocalFilters(clearedFilters)
    onFiltersChange(clearedFilters)
    setOpen(false)
  }

  const hasActiveFilters = filters.email || filters.firstName || filters.isActive !== undefined

  const activeFiltersCount = [filters.email, filters.firstName, filters.isActive !== undefined]
    .filter(Boolean).length

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2">
        <DialogTrigger asChild>
          <Button variant="outline" size="default">
            <Filter className="h-4 w-4 mr-2" />
            Filtry
            {activeFiltersCount > 0 && (
              <span className="ml-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </DialogTrigger>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            <X className="h-4 w-4 mr-1" />
            Vymazat filtry
          </Button>
        )}
      </div>

      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Filtrovat uživatele</DialogTitle>
          <DialogDescription>
            Nastavte filtry pro zobrazení požadovaných uživatelů
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Email */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="filter-email" className="text-right">
              Email
            </Label>
            <Input
              id="filter-email"
              placeholder="Hledat podle emailu..."
              value={localFilters.email || ''}
              onChange={(e) => handleFilterChange('email', e.target.value || undefined)}
              className="col-span-3"
            />
          </div>

          {/* Jméno */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="filter-name" className="text-right">
              Jméno
            </Label>
            <Input
              id="filter-name"
              placeholder="Hledat podle jména..."
              value={localFilters.firstName || ''}
              onChange={(e) => handleFilterChange('firstName', e.target.value || undefined)}
              className="col-span-3"
            />
          </div>

          {/* Stav */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="filter-status" className="text-right">
              Stav
            </Label>
            <Select
              value={
                localFilters.isActive === undefined
                  ? 'all'
                  : localFilters.isActive
                  ? 'active'
                  : 'inactive'
              }
              onValueChange={(value) => {
                if (value === 'all') {
                  handleFilterChange('isActive', undefined)
                } else {
                  handleFilterChange('isActive', value === 'active')
                }
              }}
            >
              <SelectTrigger id="filter-status" className="col-span-3">
                <SelectValue placeholder="Všechny" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny</SelectItem>
                <SelectItem value="active">Aktivní</SelectItem>
                <SelectItem value="inactive">Neaktivní</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Zrušit
          </Button>
          <Button type="button" onClick={handleApplyFilters}>
            Použít filtry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
